/**
 * Snapshotter diff/apply — unified change stack entries.
 */
import type { Object3D, Scene } from "three";
import type { HistoryHost } from "./History";
import {
  CapturedSubject,
  captureAll,
  fieldsEqual,
  Snapshotter,
} from "./snapshots";

export type HistoryDir = "undo" | "redo";

export interface ChangeOptions {
  effect?: (dir: HistoryDir) => void;
  coalesceKey?: string;
  txn?: OpenTransaction;
}

export interface FieldChange {
  subject: unknown;
  snapshotter: Snapshotter;
  before: unknown;
  after: unknown;
}

export interface ChangeEntry {
  kind: "change";
  label: string;
  changes: FieldChange[];
  effect?: (dir: HistoryDir) => void;
  coalesceKey?: string;
  time: number;
}

export interface ScopeEntry {
  kind: "scope";
  label: string;
  entries: ChangeEntry[];
  effect?: (dir: HistoryDir) => void;
}

export type StackEntry = ChangeEntry | ScopeEntry | DocumentEntry;

export interface DocumentEntry {
  kind: "document";
  label: string;
  before: Record<string, import("./SugarObject").SugarStateSlice>;
  after: Record<string, import("./SugarObject").SugarStateSlice>;
  time: number;
}

export interface OpenTransaction {
  subjects: unknown[];
  before: CapturedSubject[];
}

export const COALESCE_MS = 400;

export function openTransaction(subjects: unknown[]): OpenTransaction {
  return { subjects, before: captureAll(subjects) };
}

export function diffSnapshots(
  before: CapturedSubject[],
  after: CapturedSubject[],
): FieldChange[] {
  const changes: FieldChange[] = [];
  before.forEach((b, oi) => {
    const a = after[oi];
    b.fields.forEach((bf, fi) => {
      const af = a.fields[fi];
      if (!af || af.snapshotter !== bf.snapshotter) return;
      if (!fieldsEqual(bf.snapshotter, bf.value, af.value)) {
        changes.push({
          subject: b.subject,
          snapshotter: bf.snapshotter,
          before: bf.value,
          after: af.value,
        });
      }
    });
  });
  return changes;
}

export function buildChangeEntry(
  label: string,
  txn: OpenTransaction,
  opts: ChangeOptions = {},
): ChangeEntry | null {
  const after = captureAll(txn.subjects);
  const changes = diffSnapshots(txn.before, after);
  if (changes.length === 0 && !opts.effect) return null;
  return { kind: "change", label, changes, ...opts, time: performance.now() };
}

export function tryCoalesceChange(
  top: ChangeEntry,
  entry: ChangeEntry,
): boolean {
  if (
    !entry.coalesceKey ||
    top.coalesceKey !== entry.coalesceKey ||
    entry.time - top.time > COALESCE_MS
  ) {
    return false;
  }

  mergeChangeInto(top, entry);
  return true;
}

/** `entry`'yi `top`'a katar: her alanın en eski "before"u korunur, "after" güncellenir. */
export function mergeChangeInto(top: ChangeEntry, entry: ChangeEntry): void {
  for (const c of entry.changes) {
    const ex = top.changes.find(
      (x) => x.subject === c.subject && x.snapshotter === c.snapshotter,
    );
    if (ex) ex.after = c.after;
    else top.changes.push(c);
  }
  top.effect = entry.effect ?? top.effect;
  top.time = entry.time;
}

export function applyChangeEntry(
  entry: ChangeEntry,
  dir: HistoryDir,
  host: HistoryHost,
): void {
  for (const c of entry.changes) {
    const value = dir === "undo" ? c.before : c.after;
    c.snapshotter.restore(c.subject, value, dir);
  }
  entry.effect?.(dir);
  validateSelection(host);
  host.dispatchCustomEvent("scene-mutated", {
    detail: host.activeScene,
  });
}

function validateSelection(host: HistoryHost): void {
  const sel = host.getSystem?.("selection") as
    | { selected: Object3D | null; clear(): void }
    | undefined;
  const obj = sel?.selected;
  if (!obj) return;
  const scene = host.activeScene as Scene | undefined;
  let root: Object3D | null = obj;
  while (root && root.parent) root = root.parent;
  if (root !== scene) sel!.clear();
}
